import { AysClinicIdentifier } from './clinic-identifier';

/**
 * Helpers around the opaque `clinicSn` string used by the SN-only clinics-api.
 *
 * A `clinicSn` is contractually opaque and comes in two documented forms:
 *  - iNEK Standortnummer, 9 digits: `77XXXX0YY` (e.g. `770001001`)
 *  - synthetic legacy id for short-SN clinics: `L-{ik}-{sn}` (e.g. `L-260970082-1`)
 *
 * The regexes below are heuristics for the legacy-redirect logic ONLY. The string
 * must never be validated against them before an API call — the backend owns the
 * format and may extend it.
 */
export class AysClinicSn {
  /** 9-digit iNEK Standortnummer: `77` + 4 digits + `0` + 2 digits. */
  private static readonly INEK_PATTERN = /^77\d{4}0\d{2}$/;

  /** Synthetic legacy id: `L-{ik}-{sn}`. */
  private static readonly LEGACY_PATTERN = /^L-\d+-\d+$/;

  public static isInekSn(clinicSn: string): boolean {
    return this.INEK_PATTERN.test(clinicSn);
  }

  public static isLegacySn(clinicSn: string): boolean {
    return this.LEGACY_PATTERN.test(clinicSn);
  }

  /**
   * Build the synthetic legacy `clinicSn` for a short-SN clinic from its old
   * composite (ik, sn). Both parts are normalised with parseInt so that route
   * params carrying leading zeros (e.g. `sn = '01'`) match the backend form,
   * which renders the raw integers (`L-260970082-1`).
   */
  public static fromLegacyParts(
    ik: string | number,
    sn: string | number,
  ): string {
    return `L-${parseInt(ik.toString(), 10)}-${parseInt(sn.toString(), 10)}`;
  }

  /**
   * Resolve a `clinicSn` from a raw (ik, sn) reference — mirrors the backend
   * `ClinicSnTranslation`. A 9-digit `sn` is already an iNEK Standortnummer and
   * is returned as-is; anything shorter is a legacy short SN and is rendered as
   * the synthetic `L-{ik}-{sn}` form (see {@link fromLegacyParts}).
   */
  public static fromReference(
    ik: string | number,
    sn: string | number,
  ): string {
    const snStr = sn.toString();

    if (/^\d{9}$/.test(snStr)) {
      return snStr;
    }

    return this.fromLegacyParts(ik, sn);
  }

  /**
   * Resolve the opaque `clinicSn` for a clinic identifier (`ik` + `sn`).
   *
   * `sn` may already carry an opaque `clinicSn` — the SN-native clinic search
   * flattens it into `AysClinicIdentifier.sn` — in which case it is used as-is;
   * a short legacy sub-number is rendered as `L-{ik}-{sn}` via
   * {@link fromReference}. Mirrors the resolution used by the SN clinic
   * services (e.g. `AysClinicService#handDisinfectionUsage`).
   */
  public static fromIdentifier(
    ik: string | number,
    sn: string | number,
  ): string {
    const snStr = sn.toString();
    return this.isLegacySn(snStr) ? snStr : this.fromReference(ik, snStr);
  }

  /**
   * Best-effort reverse of {@link fromIdentifier}: recover an
   * {@link AysClinicIdentifier} from an opaque `clinicSn`, used only to seed the
   * clinic-select UI's pre-selection. Only the synthetic legacy form
   * `L-{ik}-{sn}` is fully reversible; an iNEK Standortnummer carries no `ik`,
   * so it is kept in `sn` with an empty `ik` (the full name/address lookup is
   * resolved separately and is deferred to the clinics-filter SN migration).
   */
  public static toIdentifier(clinicSn: string): AysClinicIdentifier {
    const legacy = /^L-(\d+)-(\d+)$/.exec(clinicSn);
    if (legacy) {
      return new AysClinicIdentifier(legacy[1], legacy[2]);
    }
    return new AysClinicIdentifier('', clinicSn);
  }
}
